import { createApi } from '@reduxjs/toolkit/query/react';

import {
  GetBulkResponse,
  GetBulksResponse,
  GetBulkPayload,
  GetBulksPayload,
  GetBulkForExportPayload,
  GetBulkForExportResponse,
  GetBulkFiltersResponse,
  GetJiraBulkResponse,
  GetJiraBulkPayload
} from '../interfaces/bulk.interface';
import { RunTestResultResponse, RunTestsBody } from '../interfaces/test.interface';
import { baseQueryWithReAuth } from './api';
import { createHeaders } from '../utils/createHeaders';

const BASE_URL = `${import.meta.env.VITE_BACKEND_URL}/api/athena/bulk`;

export const apiBulk = createApi({
  reducerPath: 'apiBulk',
  baseQuery: (...args) => baseQueryWithReAuth(...args, { baseUrl: BASE_URL }),
  tagTypes: ['Bulks', 'Bulk', 'runBulk', 'JiraBulks'],
  endpoints: (build) => ({
    getBulks: build.query<GetBulksResponse, GetBulksPayload>({
      query: (body) => ({
        url: '/',
        method: 'POST',
        body
      }),
      providesTags: ['Bulks']
    }),
    getBulk: build.query<GetBulkResponse, GetBulkPayload>({
      query: ({ id, dateTime, requester, authKey, jiraTicketId }) => {
        const headers = createHeaders(authKey);
        return {
          url: `/${id}`,
          method: 'GET',
          params: { dateTime, requester, jiraTicketId },
          headers
        };
      },
      providesTags: ['Bulk']
    }),
    getBulkForExport: build.query<GetBulkForExportResponse, GetBulkForExportPayload>({
      query: ({ id, authKey }) => {
        const headers = createHeaders(authKey);
        return {
          url: `/export/${id}`,
          method: 'GET',
          headers
        };
      },
      providesTags: ['Bulk']
    }),
    runBulk: build.mutation<RunTestResultResponse, { data: RunTestsBody; authKey?: string }>({
      query: ({ data, authKey }) => {
        const headers = createHeaders(authKey);
        return {
          url: '/run',
          method: 'POST',
          body: data,
          headers
        };
      },
      invalidatesTags: ['runBulk']
    }),
    getBulksFilters: build.query<GetBulkFiltersResponse, void>({
      query: () => ({
        url: `/filters`,
        method: 'GET'
      }),
      providesTags: ['Bulk']
    }),
    getJiraBulks: build.query<GetJiraBulkResponse, GetJiraBulkPayload>({
      query: (body) => ({
        url: `/jira`,
        method: 'POST',
        body
      }),
      providesTags: ['JiraBulks']
    })
  })
});

export const {
  useGetBulksQuery,
  useGetBulkQuery,
  useGetBulkForExportQuery,
  useLazyGetBulkForExportQuery,
  useRunBulkMutation,
  useGetBulksFiltersQuery,
  useGetJiraBulksQuery
} = apiBulk;
